// Paketler sayfası sağ paneli (P2 yerleşimi, spec §3.2): başlık (ad, sektör, taslağı yansıtan
// sayaçlar, ✦ AI ile öner · + Gösterge ekle · ⋯) + satır içi uyarılar (tek amber satır) + paket içi
// arama/süzgeç + TEK ağaç (yalnız paket içeriği) + seçili kalemin ayrıntı paneli (≥1280 px yan
// sütun, dar ekranda satır altı) + yapışkan taslak şeridi. Taslak YEREL durumdur (store'a girmez;
// `lib/kpi-package-draft.ts`); Kaydet → PUT (iyimser jeton `updatedAt`). 409 KPI_PACKAGE_STALE →
// bant "yeniden yükle" (taslak korunur; yükleme taslağı yeni kalemlere yeniden uygular). Diğer
// hatalar `packageErrorText` + etkilenen gösterge adları.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type KpiMetric, type KpiPackage } from "@mahir/shared";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../../api/client";
import {
  getKpiPackage,
  putKpiPackageItems,
  updateKpiPackage,
  type KpiPackageDetail,
} from "../../api/kpi";
import {
  addDraftItems,
  draftDelta,
  draftCounts,
  draftItemsBody,
  draftMemberIds,
  effectiveDefaults,
  emptyDraft,
  isDraftDirty,
  missingMemberIds,
  rebaseDraft,
  requiredByOf,
  setDraftDefaults,
  toggleDraftItem,
  type DraftContext,
  type PackageDraft,
} from "../../lib/kpi-package-draft";
import {
  bulkToggleCategory,
  editorCounts,
  filterMemberIds,
  rejectedItems,
  shownItemIds,
  whyInPackage,
  type PackageFilter,
} from "../../lib/kpi-package-editor";
import { KPI_PACKAGES_COPY, packageErrorText } from "../../lib/kpi-packages-copy";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ErrorState } from "../common/StateSurface";
import { useMediaQuery } from "../../hooks/use-media-query";
import { PackageAddDialog, type PackageAddTab } from "./PackageAddDialog";
import { PackageSuggestTab, usePackageSuggest } from "./PackageSuggestTab";
import { PackageDetailPanel } from "./PackageDetailPanel";
import { selectedTreeRow } from "../../lib/kpi-tree-focus";
import { PackageDraftBar } from "./PackageDraftBar";
import { PackageEditorFilters } from "./PackageEditorFilters";
import { PackageEditorHeader } from "./PackageEditorHeader";
import { PackageEditorBands, type SaveResult } from "./PackageEditorBands";
import { PackageEditorSkeleton } from "./PackageEditorSkeleton";
import { PackageEmptyStates } from "./PackageEmptyStates";
import { PackageIdentityModal } from "./PackageIdentityModal";
import { PackageMembersTree } from "./PackageMembersTree";
import { helpText } from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY;

/** µs metin zaman damgası ("2026-11-01 10:20:30.123456+00") → yerel kısa tarih. */
export function formatPackageStamp(stamp: string): string {
  const iso = stamp.replace(" ", "T").replace(/([+-]\d{2})$/u, "$1:00");
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? stamp.slice(0, 16)
    : d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

export interface PackageEditorProps {
  packageId: string;
  metrics: readonly KpiMetric[] | null;
  ctx: DraftContext;
  canManage: boolean;
  frameworkName: (id: string) => string | null;
  onDirtyChange: (dirty: boolean) => void;
  /** paket yazıldı (liste önbelleği tazelenir) */
  onChanged: () => void;
  /** tek kaydırıcı: ağaç sayfa gövdesindeki kalan yüksekliği doldurur */
  fill?: boolean;
  narrow?: boolean;
}

export function PackageEditor(p: PackageEditorProps): JSX.Element {
  const { packageId, ctx, canManage } = p;
  const [detail, setDetail] = useState<KpiPackageDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<PackageDraft>(emptyDraft);
  const [filter, setFilter] = useState<PackageFilter>("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** kayıt reddinde (422 details.codes) ağaçta vurgulanan göstergeler — TODO-KPI-139/1 */
  const [errorIds, setErrorIds] = useState<ReadonlySet<string>>(() => new Set());
  const [stale, setStale] = useState(false);
  /** PUT başarılı ama ardından yeniden okuma başarısız — jeton eski, kayıt yeniden yüklenene kadar kapalı */
  const [reloadFailed, setReloadFailed] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<SaveResult | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTab, setPickerTab] = useState<PackageAddTab>("catalog");
  const navigate = useNavigate();
  const wide = useMediaQuery("(min-width: 1280px)");
  const suggest = usePackageSuggest(packageId, pickerOpen);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const onDirtyRef = useRef(p.onDirtyChange);
  onDirtyRef.current = p.onDirtyChange;

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setLoadError(null);
    setDraft(emptyDraft());
    setStale(false);
    setReloadFailed(false);
    setSaveError(null);
    setErrorIds(new Set());
    setNotice(null);
    setResult(null);
    setFilter("all");
    setSearch("");
    setSelectedId(null);
    getKpiPackage(packageId)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch((e: unknown) => {
        if (alive) setLoadError(e instanceof Error ? e.message : C.page.loadOneFailed);
      });
    return () => {
      alive = false;
    };
  }, [packageId]);

  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => setResult(null), 6000);
    return () => clearTimeout(t);
  }, [result]);

  const base = useMemo(() => detail?.items ?? [], [detail]);
  // istek sürerken yapılan değişiklik kayıt sonrası kaybolmasın: güncel taslak ref'ten okunur
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const dirty = isDraftDirty(base, draft);
  useEffect(() => onDirtyRef.current(dirty), [dirty]);
  const members = useMemo(() => new Set(draftMemberIds(base, draft)), [base, draft]);
  const requiredBy = useMemo(() => requiredByOf(members, ctx), [members, ctx]);
  const deprecatedIds = useMemo(
    () => [...members].filter((id) => ctx.byId.get(id)?.status === "deprecated"),
    [members, ctx],
  );
  const missing = useMemo(() => missingMemberIds(members, ctx), [members, ctx]);
  const shownIds = useMemo(() => shownItemIds(base, draft), [base, draft]);
  const filterIds = useMemo(
    () =>
      filter === "all" && search.trim() === ""
        ? null
        : filterMemberIds(shownIds, filter, search, base, draft, ctx),
    [filter, search, shownIds, base, draft, ctx],
  );
  const counts2 = useMemo(() => editorCounts(base, draft, ctx), [base, draft, ctx]);
  const metricById = useMemo(() => new Map((p.metrics ?? []).map((m) => [m.id, m])), [p.metrics]);
  const labelOf = useCallback((id: string) => ctx.byId.get(id)?.label ?? id, [ctx]);
  const openPicker = useCallback((tab: PackageAddTab) => {
    setPickerTab(tab);
    setPickerOpen(true);
  }, []);

  if (loadError) return <ErrorState message={loadError} />;
  if (!detail || p.metrics === null) return <PackageEditorSkeleton />;
  const pkg = detail.package;
  const archived = pkg.status === "archived";
  const editable = canManage && !archived && !saving;
  const readOnlyReason = !canManage ? C.page.readOnlyNote : archived ? C.page.archivedNote : null;
  const counts = draftCounts(base, draft);
  const fw = pkg.frameworkId ? p.frameworkName(pkg.frameworkId) : null;
  const selMetric =
    selectedId && shownIds.includes(selectedId) ? metricById.get(selectedId) : undefined;
  const detail2 = selMetric ? (
    <PackageDetailPanel
      metric={selMetric}
      why={whyInPackage(selMetric.id, base, draft, ctx)}
      isMember={members.has(selMetric.id)}
      value={effectiveDefaults(base, draft, selMetric.id)}
      disabled={!editable}
      inline={!wide}
      onChange={(patch) => setDraft((d) => setDraftDefaults(base, d, selMetric.id, patch))}
      onClose={() => {
        const row = selectedTreeRow(document);
        setSelectedId(null);
        requestAnimationFrame(() => row?.focus());
      }}
    />
  ) : null;

  /** Paketi yeniden okur ve taslağı yeni kalemlere uygular (`next` verilirse o taslak; sessiz = not yok). */
  async function reload(next?: PackageDraft): Promise<void> {
    setReloading(true);
    try {
      const d = await getKpiPackage(packageId);
      const r = rebaseDraft(d.items, next ?? draftRef.current, ctx);
      setDetail(d);
      setDraft(r.draft);
      setStale(false);
      setReloadFailed(false);
      setSaveError(null);
      setNotice(
        next !== undefined
          ? null
          : r.dropped.length > 0
            ? `${C.page.rebased}. ${C.page.rebaseDropped(r.dropped.map(labelOf).join(", "))}`
            : C.page.rebased,
      );
      p.onChanged();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : C.page.loadOneFailed);
    } finally {
      setReloading(false);
    }
  }

  async function save(): Promise<void> {
    const sent = draft;
    setSaving(true);
    setSaveError(null);
    setErrorIds(new Set());
    setNotice(null);
    let res: Awaited<ReturnType<typeof putKpiPackageItems>>;
    try {
      res = await putKpiPackageItems(packageId, {
        items: draftItemsBody(base, sent),
        expectedUpdatedAt: pkg.updatedAt,
      });
    } catch (e) {
      if (e instanceof ApiError && e.code === "KPI_PACKAGE_STALE") setStale(true);
      else if (e instanceof ApiError) {
        const rejected = rejectedItems(e.details, ctx);
        const names = rejected.names.join(", ");
        const text = packageErrorText(e.code, e.message, e.details);
        setSaveError(names ? `${text} (${names})` : text);
        setErrorIds(rejected.ids);
      } else setSaveError(packageErrorText("", e instanceof Error ? e.message : undefined));
      setSaving(false);
      return;
    }
    const auto = res.autoAdded.map((a) =>
      C.page.autoAddedItem(
        labelOf(a.metricId),
        a.requiredBy.map((code) => ctx.byCode.get(code)?.label ?? code).join(", "),
      ),
    );
    setResult({
      deps: res.autoAdded.length,
      sets: pkg.usedBySetCount,
      autoAdded: auto.length > 0 ? C.page.autoAddedLine(auto.join("; ")) : null,
    });
    p.onChanged();
    try {
      const d = await getKpiPackage(packageId);
      // gönderilen işlemler bitti; yalnız istek sürerken yapılan fark yeni kalemlere uygulanır
      const r = rebaseDraft(d.items, draftDelta(sent, draftRef.current), ctx);
      setDetail(d);
      setDraft(r.draft);
      if (r.dropped.length > 0) {
        setNotice(C.page.rebaseDropped(r.dropped.map(labelOf).join(", ")));
      }
    } catch {
      setReloadFailed(true);
    } finally {
      setSaving(false);
    }
  }

  async function setStatus(status: KpiPackage["status"]): Promise<void> {
    setArchiving(true);
    setSaveError(null);
    try {
      const next = await updateKpiPackage(packageId, { status });
      setDetail((d) => (d ? { ...d, package: next } : d));
      setConfirmArchive(false);
      p.onChanged();
    } catch (e) {
      setSaveError(
        e instanceof ApiError
          ? packageErrorText(e.code, e.message, e.details)
          : packageErrorText("", e instanceof Error ? e.message : undefined),
      );
      setConfirmArchive(false);
    } finally {
      setArchiving(false);
    }
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="package-editor"
      style={p.fill ? { flex: 1, minHeight: 0 } : undefined}
    >
      <PackageEditorHeader
        pkg={pkg}
        total={counts2.total}
        auto={counts2.auto}
        stamp={formatPackageStamp(pkg.updatedAt)}
        frameworkName={fw}
        archived={archived}
        canManage={canManage}
        dirty={dirty}
        busy={saving}
        archiving={archiving}
        onAi={() => openPicker("suggest")}
        onAdd={() => openPicker("catalog")}
        onIdentity={() => setIdentityOpen(true)}
        onForkSet={() => void navigate(`/clients/kpi/setler?packageId=${packageId}`)}
        onArchiveToggle={() =>
          archived
            ? void setStatus("active")
            : pkg.usedBySetCount > 0
              ? setConfirmArchive(true)
              : void setStatus("archived")
        }
      />

      <PackageEditorBands
        readOnlyReason={readOnlyReason}
        stale={stale}
        reloadFailed={reloadFailed}
        reloading={reloading}
        onReload={() => void reload()}
        saveError={saveError}
        notice={notice}
        saved={result}
        deprecatedCount={deprecatedIds.length}
        warningFilterOn={filter === "warning"}
        onToggleWarningFilter={() => setFilter((f) => (f === "warning" ? "all" : "warning"))}
        missingCount={missing.length}
        onAddMissing={editable ? () => setDraft((d) => addDraftItems(base, d, missing, ctx)) : null}
      />

      {members.size === 0 && base.length === 0 ? (
        <PackageEmptyStates
          kind="noMembers"
          canManage={canManage && !archived}
          {...(saving
            ? {}
            : {
                onAddFromCatalog: () => openPicker("catalog"),
                onSuggest: () => openPicker("suggest"),
              })}
        />
      ) : (
        <>
          <PackageEditorFilters
            search={search}
            onSearch={setSearch}
            filter={filter}
            onFilter={setFilter}
            counts={counts2}
          />
          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: wide ? "minmax(0, 1fr) 340px" : "minmax(0, 1fr)",
              ...(p.fill ? { flex: 1, minHeight: 0, gridTemplateRows: "minmax(0, 1fr)" } : {}),
            }}
          >
            <div style={p.fill ? { display: "flex", flexDirection: "column", minHeight: 0 } : {}}>
              <PackageMembersTree
                metrics={p.metrics}
                ctx={ctx}
                base={base}
                draft={draft}
                editable={editable}
                readOnlyReason={readOnlyReason}
                filterIds={filterIds}
                errorIds={errorIds}
                onClearFilters={() => {
                  setFilter("all");
                  setSearch("");
                }}
                requiredBy={requiredBy}
                selectedId={selMetric?.id ?? null}
                onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
                {...(!wide && detail2 ? { detailBelow: detail2 } : {})}
                onToggle={(id, on) => setDraft((d) => toggleDraftItem(base, d, id, on, ctx))}
                onToggleCategory={(id) =>
                  setDraft((d) =>
                    bulkToggleCategory(base, d, id, new Set(shownItemIds(base, d)), ctx, filterIds),
                  )
                }
                {...(p.fill ? { fill: true } : {})}
                {...(p.narrow ? { narrow: true } : {})}
              />
            </div>
            {wide ? (
              <aside style={{ minHeight: 0, overflowY: "auto" }}>
                {detail2 ?? <p style={helpText}>{C.detail.placeholder}</p>}
              </aside>
            ) : null}
          </div>
        </>
      )}

      {dirty ? (
        <PackageDraftBar
          counts={counts}
          from={base.length}
          to={counts2.total}
          saving={saving}
          blocked={stale || reloadFailed}
          onSave={() => void save()}
          onDiscard={() => {
            setSaveError(null);
            // eski jetonla bir sonraki kayıt yine reddedilmesin: bayatsa paketi de yeniden oku
            if (stale || reloadFailed) void reload(emptyDraft());
            else setDraft(emptyDraft());
          }}
        />
      ) : null}

      <PackageAddDialog
        open={pickerOpen}
        initialTab={pickerTab}
        pkg={pkg}
        metrics={p.metrics}
        ctx={ctx}
        members={members}
        suggestTab={(api) => (
          <PackageSuggestTab
            controller={suggest}
            metrics={p.metrics ?? []}
            api={api}
            narrow={api.narrow}
          />
        )}
        suggestResult={suggest.state.kind === "done" ? suggest.state.res : null}
        onClose={() => setPickerOpen(false)}
        onAdd={(picks) => {
          setDraft((d) =>
            addDraftItems(
              base,
              d,
              picks.map((x) => x.metricId),
              ctx,
              new Map(picks.map((x) => [x.metricId, x])),
            ),
          );
          setPickerOpen(false);
          setFilter("all");
        }}
      />
      <PackageIdentityModal
        open={identityOpen}
        pkg={pkg}
        onClose={() => setIdentityOpen(false)}
        onSaved={(next) => {
          setDetail((d) => (d ? { ...d, package: next } : d));
          setIdentityOpen(false);
          p.onChanged();
        }}
      />
      <ConfirmDialog
        open={confirmArchive}
        title={C.page.archiveConfirmTitle}
        body={C.page.archiveConfirm(pkg.usedBySetCount)}
        confirmLabel={C.page.archive}
        cancelLabel={C.page.cancel}
        tone="neutral"
        busy={archiving}
        onConfirm={() => void setStatus("archived")}
        onCancel={() => setConfirmArchive(false)}
      />
    </div>
  );
}
